import { useEffect, useState } from "react";
import axios from "axios";
import { BadgeEuro, BookOpenText, Check, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { MSP_PDF, TESSERE_SPIEGAZIONI } from "../../data/tessere";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Dialog interattivo: categorie + prezzi (selezionabili) e spiegazioni. */
export const TessereCatalog = ({ open, tab, onOpenChange, tessere, selected, onSelect }) => {
  const [gruppi, setGruppi] = useState([]);
  useEffect(() => {
    axios.get(`${API}/tessere/gruppi`).then((r) => setGruppi(r.data)).catch(() => {});
  }, []);

  const scegli = (id) => {
    onSelect(id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="tessere-dialog"
        className="max-w-3xl w-[calc(100%-2rem)] max-h-[90vh] overflow-hidden flex flex-col bg-neutral-950 border-neutral-800 text-neutral-100 p-0 gap-0 rounded-none"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-neutral-800 text-left">
          <div className="mono-label text-xs text-red-500 uppercase tracking-wider">Tesseramento 2026 &bull; Settore Motori MSP Italia</div>
          <DialogTitle className="font-display text-2xl font-black uppercase text-white">Categorie, prezzi e spiegazioni</DialogTitle>
          <DialogDescription className="text-xs text-neutral-400">
            Con il Legend Team possono partecipare tutti: scegli la categoria giusta e paga online. Le quote includono affiliazione e coperture assicurative.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={tab} key={tab} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="mx-6 mt-4 grid grid-cols-2 bg-neutral-900 border border-neutral-800 rounded-none h-auto p-1">
            <TabsTrigger value="prezzi" data-testid="tab-prezzi" className="rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white mono-label text-xs uppercase tracking-wider py-2 gap-2">
              <BadgeEuro className="w-4 h-4" /> Categorie e prezzi
            </TabsTrigger>
            <TabsTrigger value="spiegazioni" data-testid="tab-spiegazioni" className="rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white mono-label text-xs uppercase tracking-wider py-2 gap-2">
              <BookOpenText className="w-4 h-4" /> Spiegami le categorie
            </TabsTrigger>
          </TabsList>

          <TabsContent value="prezzi" className="flex-1 min-h-0 overflow-y-auto px-6 py-5 mt-0 space-y-6">
            {gruppi.map((g) => {
              const items = tessere.filter((t) => t.gruppo === g.id);
              if (!items.length) return null;
              return (
                <div key={g.id} data-testid={`gruppo-${g.id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-neutral-800 pb-2 mb-2">
                    <h4 className="font-display text-sm font-black uppercase text-white">{g.label}</h4>
                    <span className="mono-label text-[10px] uppercase tracking-wider text-neutral-500">{g.visita}</span>
                  </div>
                  <ul className="divide-y divide-neutral-900">
                    {items.map((t) => {
                      const active = selected === t.id;
                      return (
                        <li key={t.id}>
                          <button
                            type="button"
                            data-testid={`scegli-${t.id}`}
                            onClick={() => scegli(t.id)}
                            className={`w-full grid grid-cols-[1fr_auto_auto] items-center gap-4 py-2.5 px-2 text-left transition-colors ${
                              active ? "bg-red-950/40" : "hover:bg-neutral-900"
                            }`}
                          >
                            <span className="min-w-0">
                              <span className="block text-sm font-bold text-white uppercase">{t.label}</span>
                              <span className="block text-xs text-neutral-400">{t.disciplina}</span>
                            </span>
                            <span className="mono-label text-sm font-bold text-red-500">{t.prezzo.toFixed(0)} €</span>
                            <span className={`w-20 text-center mono-label text-[10px] uppercase tracking-wider px-2 py-1 border ${
                              active ? "bg-red-600 border-red-600 text-white" : "border-neutral-700 text-neutral-300"
                            }`}>
                              {active ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" /> Scelta</span> : "Scegli"}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="spiegazioni" className="flex-1 min-h-0 overflow-y-auto px-6 py-5 mt-0">
            <Accordion type="single" collapsible defaultValue="team" className="w-full">
              {TESSERE_SPIEGAZIONI.map((s) => (
                <AccordionItem key={s.id} value={s.id} data-testid={`spiegazione-${s.id}`} className="border-neutral-800">
                  <AccordionTrigger className="text-left text-sm font-bold uppercase text-white hover:text-red-500 hover:no-underline">
                    {s.titolo}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-neutral-300 leading-relaxed">{s.testo}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </TabsContent>
        </Tabs>

        <div className="px-6 py-4 border-t border-neutral-800 flex flex-wrap items-center justify-between gap-3 bg-neutral-900">
          <a
            data-testid="msp-pdf-link"
            href={MSP_PDF}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mono-label text-xs uppercase tracking-wider text-neutral-300 hover:text-red-500"
          >
            <FileText className="w-4 h-4" /> Listino ufficiale MSP 2026 (PDF)
          </a>
          <span className="mono-label text-[10px] uppercase tracking-wider text-neutral-500">Prezzi comprensivi di affiliazione e assicurazione</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
